<template>
  <section>
      <div v-if="pageLoaded" class="container-fluid">
      <div class="pricing-table__wrapper" data-gtm-context="Pricing">
        <div class="selector-wrapper">
          <p class="mb-4 strong">How many contacts do you have?</p>
          <SliderSelector
            @setSubscriberCount="setSubscriberCount"
            :count="subscriberCount"
            :step="1"
            :min="0"
            :max="markers.length - 1"
          ></SliderSelector>
        </div>
        <div class="toggles-container mb-2">
          <ToggleSwitch
            optionOne="USD"
            optionTwo="GBP"
            :value="currency"
            :onSwitch="toggleCurrency"
          ></ToggleSwitch>
          <ToggleSwitch
            optionOne="Annual <span style='color: var(--color-indigo-600)'>(save up to 10%)</span>"
            optionTwo="monthly"
            :value="frequency"
            :onSwitch="toggleFrequency"
          ></ToggleSwitch>
        </div>
        <div class="pricing-tiers__wrapper">

          <PricingCard
            :currencySymbol="currencySymbol"
            :perFreq="perFreq"
            title="Starter"
            plan-description="Perfect if you're just starting out with marketing and want to take things one step at a time."
            :price="getPlanPrice(2)"
            :isDisabled="isPlanDisabled(2)"
            :limitations="[]"
            :features="[
              'Up to 3 users',
              'Generate attractive branded email templates with AI',
              'Create engaging copy using AI Content Assistant',
              'Optimised email sending',
              'Schedule social posts in advance',
              'Enable live chat on your website',
              'SMS Campaigns (add-on)'
            ]"
            @openComparisonModal="openComparisonModal"
          ></PricingCard>

          <PricingCard
            :currencySymbol="currencySymbol"
            :perFreq="perFreq"
            :mostPopular="true"
            title="Growth"
            plan-description="Ideal for companies looking to scale marketing activity across different channels."
            :price="getPlanPrice(3)"
            :isDisabled="isPlanDisabled(3)"
            :limitations="[]"
            previousPlanText="Everything on Starter, plus:"
            :features="[
              'Up to 5 users',
              'Engagement-based email sending',
              'A/B test your campaigns',
              'Manage multiple social accounts',
              'Create unlimited Automations',
              'Smart email sending'
            ]"
            @openComparisonModal="openComparisonModal"
          ></PricingCard>

          <PricingCard
            :currencySymbol="currencySymbol"
            :perFreq="perFreq"
            title="Advanced"
            plan-description="Ideal for experienced marketers looking for an affordable all-in-one marketing tool."
            :price="getPlanPrice(6)"
            :isDisabled="isPlanDisabled(6)"
            :limitations="[]"
            previousPlanText="Everything on Growth, plus:"
            :features="[
              'Up to 10 users',
              'AI Assistant for your website',
              'Web Tracking for up to 10 sites'
            ]"
            @openComparisonModal="openComparisonModal"
          ></PricingCard>

          <PricingCard
            :currencySymbol="currencySymbol"
            :perFreq="perFreq"
            title="Ultimate"
            plan-description="For established marketing teams looking for a solution that slots neatly into their existing marketing strategy."
            :price="getPlanPrice(4)"
            :isDisabled="isPlanDisabled(4)"
            :customPricing="isPlanDisabled(4)"
            :limitations="[]"
            previousPlanText="Everything on Advanced, plus:"
            :features="[
              'Unlimited users',
              'Unlimited access to everything Transpond has to offer',
              'Conversations Agent Reporting'
            ]"
            @openComparisonModal="openComparisonModal"
          ></PricingCard>
        </div>

        <div class="pricing-card pricing-card-freeplan col-xs-12">
          <div class="pricing-card-freeplan_left">
            <h2 class="h5 mb-1">Free</h2>
            <div class="p--sm">
              Perfect for getting started with email marketing basics.
            </div>
          </div>
          <div class="pricing-card-freeplan_right">
            <button
              v-on:click="goToFreeRegister"
              class="btn btn--secondary btn--sm "
              :disabled="isPlanDisabled('Free')"
            >
              {{ isPlanDisabled('Free') ? 'Plan Unavailable' : 'Get Started' }}
            </button>
          </div>
        </div>
        <div class="pricing-view-comparison" v-on:click="trackCompareFeatures">
          <p>Compare all plan features →</p>
        </div>
      </div>
    </div>
    <FeatureComparisonModal
      :open="showComparisonModal"
      @close="closeComparisonModal"
    ></FeatureComparisonModal>
    <SmsPricingModal
      :open="showSmsPricingModal"
      @close="closeSmsPricingModal"
    ></SmsPricingModal>
  </section>
  <section class="mt-20 center-xs">
    <div v-if="pageLoaded" class="container-fluid">
      <div class="addons-wrapper">
        <div class="addons-header">
          <div class="addons-title mb-1">
            <span></span>
            <h4>Enhance your plan with our add-ons</h4>
            <span></span>
          </div>
          <p>Available to activate from within your Transpond account</p>
        </div>
        <div class="addons-content row middle-xs">
          <div class="addons-card col-md-12 col-lg-10">
            <div class="addons-card-asset">
              <img
                src="../../assets/sms-addon-main.png"
                alt="SMS Example"
                title="SMS Example"
              />
            </div>
            <div class="addons-card-content">
              <span class="addons-card-pill mb-2">UK Customers Only</span>
              <h3>SMS add-on</h3>
              <p class="p--sm">Enhance your campaigns by sending targeted SMS messages directly to your audience. Add SMS steps to your automations or run standalone SMS campaigns. Perfect for reminders, events, promotional and offers.</p>
              <div class="addons-card-pricing">
                <div class="addons-card-pricing-kicker">
                  Starting from
                </div>
                <div class="addons-card-pricing-cost">
                  <sup class="p--xs">£</sup>
                  <h4>{{ getSmsAddonPrice() }}</h4>
                  <sub>{{ getSmsAddonFrequencyText() }}</sub>
                </div>
              </div>
              <div class="addons-card-meta mt-2">
                <router-link to="/features/sms/" class="btn btn--primary btn--sm">
                  Learn more
                </router-link>
                <button class="btn btn--secondary btn--sm" @click="openSmsPricingModal">
                  View Pricing
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import PricingCard from "./PricingCard";
import ToggleSwitch from "./ToggleSwitch";
import SliderSelector from "./SliderSelector";
import { getPricingData } from "../../dataRequest";
import TrackingHelpers from "@/functions/tracking";
import { gtmRegistrationStart, gtmTracking } from "@/functions/gtm-tracking";
import FeatureComparisonModal from "@/components/Modal/FeatureComparisonModal";
import SmsPricingModal from "@/components/Modal/SmsPricingModal";
export default {
  //used for ref above
  components: { PricingCard, ToggleSwitch, SliderSelector, FeatureComparisonModal, SmsPricingModal },
  data() {
    return {
      pricePlans: [],
      currency: "GBP",
      currencySymbol: "£",
      frequency: "annually",
      perFreq: "year",
      subscriberCount: 0,
      pricingLoaded: false,
      isDisabled: false,
      customPricing: false,
      markers: [],
      pageLoaded: false,
      showComparisonModal: false,
      showSmsPricingModal: false
    };
  },
  ///component has mounted
  created() {
    getPricingData().then((data) => {
      //contact limit, tiers available
      let flattenedPrices = [
        {contactLimit: 250,tiers: [{tier: 0, priceGbp: 0, priceUsd: 0}]}
      ] 
      data.forEach((tier)=>{
        tier.prices.forEach((price)=>{
          if(price.maxUniqueSends > 220000) return;
          const existing = flattenedPrices.find((p)=>p.contactLimit === price.maxUniqueSends);
          if(!existing){
            flattenedPrices.push({contactLimit: price.maxUniqueSends, tiers: [{tier: tier.id, priceGbp: price.monthly.priceGbp, priceUsd: price.monthly.priceUsd}]});
          }
          else
          {
            existing.tiers.push({tier: tier.id, priceGbp: price.monthly.priceGbp, priceUsd: price.monthly.priceUsd});
          }
        })
      })
      //sort by contact limit
      flattenedPrices = flattenedPrices.sort((a,b)=>a.contactLimit - b.contactLimit);
      this.subscriberCount = flattenedPrices[0].contactLimit;
      this.markers = flattenedPrices.map((p)=>p.contactLimit);
      this.pricePlans = data;
      this.pageLoaded = true;
    });
  },
  setup()
  {
    return { TrackingHelpers }
  },
  //functions
  methods: {
    openComparisonModal() {
      this.showComparisonModal = true;
    },
    trackCompareFeatures() {
      gtmTracking("CTA Click", "Compare all plan features", "Pricing")();
      this.openComparisonModal();
    },
    closeComparisonModal() {
      this.showComparisonModal = false;
    },
    openSmsPricingModal() {
      this.showSmsPricingModal = true;
    },
    closeSmsPricingModal() {
      this.showSmsPricingModal = false;
    },
    calculateEmailSends(planId){
      let multiplier = 10;
      if(planId == 2) multiplier = 10;
      if(planId == 3) multiplier = 10;
      if(planId == 6) multiplier = 15;
      if(planId == 4) multiplier = 20;
      const pDisabled = this.isPlanDisabled(planId);
      let calcContacts = this.subscriberCount;
      if(pDisabled) {
        //get the max number of contacts in the tier
        calcContacts = this.pricePlans.find((p)=>p.id === planId).prices[this.pricePlans.find((p)=>p.id === planId).prices.filter(f=>f.maxUniqueSends < 200001).length - 1].maxUniqueSends;
      }
      const newVal = (multiplier * calcContacts).toLocaleString();
      return newVal;

    },
    toggleCurrency() {
      this.currency = this.currency === "GBP" ? "USD" : "GBP";
      this.currencySymbol = this.currencySymbol === "£" ? "$" : "£";
    },
    toggleFrequency() {
      this.frequency = this.frequency === "monthly" ? "annually" : "monthly";
      this.perFreq = this.perFreq === "month" ? "year" : "month";
    },
    setSubscriberCount(count) {
      this.subscriberCount = this.markers[count];
    },
    getPlanPrice(id) {
      const plan = this.pricePlans.find((p) => p.id === id);
      if (!plan) return -1;
      let planPrices = plan.prices
        .slice()
        .sort((a, b) => a.maxUniqueSends - b.maxUniqueSends);
      let price = planPrices.find(
        (p) => p.maxUniqueSends >= this.subscriberCount
      );
      if (!price) {
        price = planPrices[planPrices.length - 1];
      }
      const currencyKey = this.currency === "USD" ? "priceUsd" : "priceGbp";
      return price[this.frequency][currencyKey];
    },
    isPlanDisabled(id) {
      const aboveFreeSubscriberLimit = this.subscriberCount > 250;
      if (aboveFreeSubscriberLimit) {
        const plan = this.pricePlans.find((p) => p.id === id);
        if (!plan) return -1;
        let price = plan.prices.find(
          (p) => p.maxUniqueSends >= this.subscriberCount
        );
        if (this.subscriberCount > 219999) {
          return (this.customPricing = true);
        }
        if (!price) {
          return (this.isDisabled = true);
        }
      }
    },
    goRegister() {
      gtmRegistrationStart("CTA Click", "Get Started", "Pricing")();
      TrackingHelpers.linkToApp("register?welcome=true");
    },
    goToFreeRegister() {
      if (this.isPlanDisabled("Free")) return;
      gtmRegistrationStart("CTA Click", "Get Started", "Pricing")();
      TrackingHelpers.linkToApp("register?welcome=true");
    },
    getSmsAddonPrice() {
      return this.frequency === "annually" ? "12.50" : "15";
    },
    getSmsAddonFrequencyText() {
      return this.frequency === "annually" ? "per month (billed annually)" : "per month";
    },
  },
};
</script>
